<script lang="ts">
    import {BarcodeScannerService} from '../../../../../barcode-scanner/barcode-scanner.service';
    import {onDestroy} from 'svelte';
    import type {Subscription} from 'rxjs';
    import type {ValueEditorSize} from '../../../../types';
    import {INPUT_GROUP_CLASS, INPUT_GROUP_HIDDEN_CLASS} from '../utils';
    import {isDefined, isUndefined} from '../../../../../utils/custom-utils';
    import {getInjector} from 'core/svelte-context/context';

    export let allowedAutomaticStart: boolean;
    export let isDisabled: boolean;
    export let size: ValueEditorSize;
    export let inputElement: HTMLInputElement;
    export let inputGroupElement: HTMLElement;

    const barcodeScannerService = getInjector().getByToken<BarcodeScannerService>(BarcodeScannerService.serviceName);

    const LOCAL_STORAGE_KEY = 'cameraScannerAllowed';

    let subscription: Subscription;
    let isDestroyed = false; // we use async initialization, so we need this flag to know current position in lifecycle
    let isScannerEnabled: boolean;

    $: if (isDefined(inputElement) && isDefined(inputGroupElement) && isUndefined(subscription)) {
        // run init after inputElement and inputGroupElement props are filled and run it only once (subscription exists)
        init();
    }

    onDestroy(() => {
        isDestroyed = true;
        subscription?.unsubscribe();
    })

    async function init() {
        if (!await barcodeScannerService.isUsable()) {
            return;
        }

        if (isDestroyed) {
            return; // stop init if component was already destroyed
        }

        showControls();

        subscription = barcodeScannerService.getScannerEnabledState$().subscribe((state) => setState(state));

        if (allowedAutomaticStart && !isScannerEnabled) {
            automaticStart();
        }
    }

    function automaticStart() {
        const enableScanner = loadEnableValue();
        if (!enableScanner) {
            return;
        }
        barcodeScannerService.startScanning();
        if (inputElement === window.document.activeElement) {
            unFocusInput();
        }
        focusInput();
    }

    function toggle(): void {
        unFocusInput(); // to unsubscribe before state switch
        barcodeScannerService.toggleScanning();
        focusInput(); // to (re)subscribe after switch
        if (allowedAutomaticStart) {
            storeEnabledValue(isScannerEnabled);
        }
    }

    function setState(isScannerEnabledSet: boolean) {
        isScannerEnabled = isScannerEnabledSet;
        if (isScannerEnabled) {
            enableInputScannerMode();
        } else {
            disableInputScannerMode();
        }
    }

    function focusInput(): void {
        inputElement.focus();
    }

    function unFocusInput(): void {
        inputElement.blur();
    }

    function storeEnabledValue(value: boolean): void {
        localStorage.setItem(LOCAL_STORAGE_KEY, String(value));
    }

    function enableInputScannerMode() {
        inputElement.readOnly = true;
    }

    function disableInputScannerMode() {
        inputElement.readOnly = false;
    }

    function showControls() {
        inputGroupElement.classList.add(INPUT_GROUP_CLASS);
        inputGroupElement.classList.remove(INPUT_GROUP_HIDDEN_CLASS);
    }

    function loadEnableValue(): boolean {
        return localStorage.getItem(LOCAL_STORAGE_KEY) === 'true';
    }
</script>

<div class="input-group-btn">
    <button type="button" class="btn btn-default input-{size}"
            class:active={isScannerEnabled}
            disabled={isDisabled}
            on:mousedown|preventDefault
            on:click={toggle}>
        {#if isScannerEnabled}
            <slot name="stop-scanning"></slot>
        {:else }
            <slot name="start-scanning"></slot>
        {/if}
    </button>
</div>

<style lang="less" global>
    .input-group-hidden {
        display:block;

        .input-group-btn {
            display:none;
        }
    }
</style>